import type ApiError from '~/types/ApiError';

export type NativeLensFailure = {
  severity: 'warning' | 'error';
  message: string;
  /** False when pressing Retry cannot change the answer, such as an unregistered page. */
  retryable: boolean;
};

const NETWORK_ERROR_PREFIX = /^Network error received \(status code \d+\):\s*/;

/**
 * Reader-facing wording for a native page whose Lens data request failed.
 *
 * request() reports HTTP failures as "Network error received (status code 404): Lens not found",
 * which tells a reader nothing they can act on. A 404 here means the native Lens has not been
 * registered in this environment, so it reads as a setup step rather than an outage, and Retry is
 * withheld because it cannot help. Anything else is treated as transient and offers Retry.
 */
export function nativeLensFailure(error: unknown, pageTitle: string): NativeLensFailure {
  const status = (error as ApiError | null)?.status;
  if (status === 404) {
    return {
      severity: 'warning',
      message: `${pageTitle} is not registered in Library for this environment yet. A reporting admin registers it under Library → Teams → Register native page.`,
      retryable: false,
    };
  }
  if (status === 403) {
    return {
      severity: 'warning',
      message: `You do not have access to ${pageTitle}. Ask a reporting admin to add you to its Library folder.`,
      retryable: false,
    };
  }
  const detail = error instanceof Error ? error.message.replace(NETWORK_ERROR_PREFIX, '').trim() : '';
  return {
    severity: 'error',
    message: detail ? `${pageTitle} could not load: ${detail}` : `${pageTitle} could not load.`,
    retryable: true,
  };
}
